import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete
} from "@mui/material";
import { Groups } from "@mui/icons-material";
import { mftCompanies } from "../../../../../../utils/consts";
import { CommentDto, Broker, VpDto } from "../../../../../../api/Api";
import { VpPicker } from "../../../../../../components/vpPicker";
import {
  useBrokerAssignees,
  useBrokerProgressOverride,
  useUpdateBroker
} from "../../../../dataHooks/brokerQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { brokerAttachmentApi } from "../../../brokerAttachmentApi";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificBroker: Broker;
};

type FormData = {
  marketAccessAssignee: string;
  brokerName: string;
  contractualMFTTeam: string;
  kycCheckOnMft: string;
  kycCheckOnBroker: string;
  agreementType: string;
  agreementReview: string;
  ndaReview: string;
  mandate: string;
  // ADHO-453: no longer edited here, the gate moved to Compliance as
  // "REMIT reporting". Kept in the form so buildBroker round-trips the stored
  // value instead of wiping it, until every broker has migrated.
  reportingRequirements: string;
  tradeConfirmation: string;
  comments: CommentDto[];
  signOffNote: string;
};

export const MarketAccess: FC<Props> = ({ specificBroker }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const roles = useRoles();
  const progressOverride = useBrokerProgressOverride(
    specificBroker,
    "marketAccess"
  );

  const marketAccessAssignees = useBrokerAssignees("marketAccess");

  // Kept out of FormData on purpose: FormData is spread wholesale into
  // marketAccessLifecycle, and the VP is a root level field.
  const [vp, setVp] = useState<VpDto | null>(specificBroker?.vp ?? null);

  const [formData, setFormData] = useState<FormData>({
    brokerName: specificBroker?.brokerName || "",
    contractualMFTTeam: specificBroker?.tradingTeam || "",
    marketAccessAssignee:
      specificBroker?.marketAccessLifecycle?.marketAccessAssignee || "",
    kycCheckOnMft: specificBroker?.marketAccessLifecycle?.kycCheckOnMft || "",
    kycCheckOnBroker:
      specificBroker?.marketAccessLifecycle?.kycCheckOnBroker || "",
    agreementType: specificBroker?.marketAccessLifecycle?.agreementType || "",
    agreementReview:
      specificBroker?.marketAccessLifecycle?.agreementReview || "",
    ndaReview: specificBroker?.marketAccessLifecycle?.ndaReview || "",
    mandate: specificBroker?.marketAccessLifecycle?.mandate || "",
    reportingRequirements:
      specificBroker?.marketAccessLifecycle?.reportingRequirements || "",
    tradeConfirmation:
      specificBroker?.marketAccessLifecycle?.tradeConfirmation || "",
    comments: specificBroker?.marketAccessLifecycle?.comments || [],
    signOffNote: specificBroker?.marketAccessLifecycle?.signOffNote || ""
  });

  const handleClose = () => {
    navigate(`/brokers/broker/${params.id}`);
  };
  const updateBroker = useUpdateBroker({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateBroker();

  const buildBroker = (values: FormData & { complete?: boolean }): Broker => {
    const { brokerName, contractualMFTTeam, ...slice } = values;
    return {
      ...specificBroker,
      brokerName: brokerName ?? specificBroker.brokerName,
      tradingTeam: contractualMFTTeam ?? specificBroker.tradingTeam,
      vp,
      marketAccessLifecycle: {
        ...specificBroker.marketAccessLifecycle,
        ...slice
      }
    };
  };

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildBroker({ ...formData, comments })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificBroker.progress?.marketAccess,
    canSetInProgress: ownsLifecycle(roles, "marketaccess"),
    buildEntity: (values, progress) => {
      const saved = buildBroker(values);
      return {
        ...saved,
        progress: { ...saved.progress, marketAccess: progress }
      };
    },
    mutate: updateBroker.mutate
  });

  const open = location.pathname.includes("marketaccess");

  const canComplete =
    formData.brokerName &&
    formData.contractualMFTTeam &&
    formData.kycCheckOnMft &&
    formData.kycCheckOnBroker &&
    formData.agreementType &&
    formData.agreementReview &&
    formData.ndaReview &&
    formData.mandate;

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Market Access"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={brokerAttachmentApi}
      headerContent={
        <>
          <Box display="flex" gap={2}>
            <Typography variant="subtitle1" component="span">
              Requested by:
            </Typography>
            <Typography variant="subtitle1" component="span" fontWeight="bold">
              {specificBroker?.contactPersonTradingTeam}
            </Typography>
            <Typography variant="subtitle1" component="span" fontWeight="bold">
              {specificBroker?.tradingTeam}
            </Typography>
          </Box>
          <Box sx={{ paddingY: 2 }}>
            <Autocomplete
              sx={{ flex: 1, alignSelf: "center" }}
              options={marketAccessAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                marketAccessAssignees.data?.find(
                  (a) => a.email === formData.marketAccessAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange("marketAccessAssignee", newValue?.email || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Market Access assignee"
                  size="medium"
                />
              )}
            />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <VpPicker value={vp} onChange={setVp} />
          </Box>
          <Divider flexItem />
        </>
      }
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateBroker.isPending
      }}
    >
      <Box sx={{ paddingTop: 1 }} gap={2} display="flex" flexDirection="column">
        <TextField
          value={formData.brokerName}
          onChange={(e) => handleChange("brokerName", e.target.value)}
          label="Broker name"
          size="medium"
          required
        />

        <FormControl fullWidth required>
          <InputLabel>Contractual MFT Team</InputLabel>
          <Select
            value={formData.contractualMFTTeam}
            onChange={(e) => handleChange("contractualMFTTeam", e.target.value)}
            label="Contractual MFT Team"
            startAdornment={
              <InputAdornment position="start">
                <Groups fontSize="small" />
              </InputAdornment>
            }
          >
            {mftCompanies.map((company) => {
              const split = company.split("MFT");
              return (
                <MenuItem key={company} value={company}>
                  <Typography variant="body1" component="span">
                    {"MFT" + split[1]}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Divider />

        <FormControl size="medium" required>
          <InputLabel>KYC Check (on MFT)</InputLabel>
          <Select
            label="KYC Check (on MFT)"
            value={formData.kycCheckOnMft}
            onChange={(e) => handleChange("kycCheckOnMft", e.target.value)}
          >
            <MenuItem value="needed">Needed</MenuItem>
            <MenuItem value="not_needed">Not needed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>KYC Check/sanction screening (on Broker)</InputLabel>
          <Select
            label="KYC Check/sanction screening (on Broker)"
            value={formData.kycCheckOnBroker}
            onChange={(e) => handleChange("kycCheckOnBroker", e.target.value)}
          >
            <MenuItem value="needed">Needed</MenuItem>
            <MenuItem value="not_needed">Not needed</MenuItem>
          </Select>
        </FormControl>

        <TextField
          value={formData.agreementType}
          onChange={(e) => handleChange("agreementType", e.target.value)}
          label="Agreement Type"
          size="medium"
          required
        />

        <FormControl size="medium" required>
          <InputLabel>Agreement Review</InputLabel>
          <Select
            label="Agreement Review"
            value={formData.agreementReview}
            onChange={(e) => handleChange("agreementReview", e.target.value)}
          >
            <MenuItem value="needed">Needed</MenuItem>
            <MenuItem value="not_needed">Not needed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>NDA Review</InputLabel>
          <Select
            label="NDA Review"
            value={formData.ndaReview}
            onChange={(e) => handleChange("ndaReview", e.target.value)}
          >
            <MenuItem value="needed">Needed</MenuItem>
            <MenuItem value="not_needed">Not needed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="medium" required>
          <InputLabel>Mandate review</InputLabel>
          <Select
            label="Mandate review"
            value={formData.mandate}
            onChange={(e) => handleChange("mandate", e.target.value)}
          >
            <MenuItem value="needed">Needed</MenuItem>
            <MenuItem value="not_needed">Not needed</MenuItem>
          </Select>
        </FormControl>

        <FormControl required size="medium">
          <InputLabel>Trade Confirmation</InputLabel>
          <Select
            label="Trade Confirmation"
            value={formData.tradeConfirmation}
            onChange={(e) => handleChange("tradeConfirmation", e.target.value)}
          >
            <MenuItem value="needed">Needed</MenuItem>
            <MenuItem value="not_needed">Not needed</MenuItem>
          </Select>
        </FormControl>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, signOffNote: value }))
          }
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
